import { useCallback, useEffect, useState } from 'react';
import { Content, Header, Link, Page, Progress, Table, TableColumn } from '@backstage/core-components';
import { discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { IconButton, Paper, Tooltip, Typography } from '@material-ui/core';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import RefreshIcon from '@material-ui/icons/Refresh';
import StopIcon from '@material-ui/icons/Stop';
import AccountTreeIcon from '@material-ui/icons/AccountTree';
import { actionAvailability, formatCpu, formatMemory, statusInfo, summary, timeAgo } from './appsFormat';

// Aplicações já implantadas pelo pipeline: ligar, desligar e reiniciar sem novo
// build (desligar = 0 réplicas; a imagem, o Service e o Ingress ficam). Lista e
// ações vêm do backend do plugin pipeline-registration (rotas /apps), só para
// quem está no grupo admin, como a página Pipelines.

type AppLink = {
  kind: 'ingress' | 'nodeport';
  url: string;
  label: string;
};

type AppRow = {
  app: string;
  status: string;
  desiredReplicas: number;
  readyReplicas: number;
  url?: string;
  links?: AppLink[];
  commit?: string;
  deployedAt?: string;
  cpuMillicores?: number;
  memoryMiB?: number;
  lastRunResult?: string;
  lastRunFinishedAt?: string;
};

type Action = 'start' | 'stop' | 'restart';

const RESULT_COLOR: Record<string, string> = {
  Succeeded: '#15803d',
  Failed: '#b91c1c',
  Error: '#b91c1c',
};

const ACTION_DONE: Record<Action, string> = {
  start: 'ligada',
  stop: 'desligada',
  restart: 'reiniciando',
};

// A lista se atualiza sozinha: o estado muda por fora (pod que sobe, pipeline que
// implanta) e depois de cada ação, sem precisar de F5.
const REFRESH_MS = 4000;

export const AppsPage = () => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [apps, setApps] = useState<AppRow[] | undefined>();
  const [loadError, setLoadError] = useState<string>();
  const [busyApp, setBusyApp] = useState<string>();
  const [actionError, setActionError] = useState<string>();
  const [actionMessage, setActionMessage] = useState<string>();

  const load = useCallback(async () => {
    try {
      const baseUrl = await discoveryApi.getBaseUrl('pipeline-registration');
      const response = await fetchApi.fetch(`${baseUrl}/apps`);
      const body = (await response.json().catch(() => ({}))) as { apps?: AppRow[]; error?: string };
      if (!response.ok) {
        throw new Error(body.error ?? `Falha ao carregar as aplicações (${response.status})`);
      }
      setApps(body.apps ?? []);
      setLoadError(undefined);
    } catch (error) {
      setLoadError((error as Error).message);
    }
  }, [discoveryApi, fetchApi]);

  useEffect(() => {
    load();
    const id = setInterval(() => {
      // Aba em segundo plano não precisa consultar o cluster.
      if (document.visibilityState === 'visible') load();
    }, REFRESH_MS);
    return () => clearInterval(id);
  }, [load]);

  const runAction = async (app: string, action: Action) => {
    setBusyApp(app);
    setActionError(undefined);
    setActionMessage(undefined);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('pipeline-registration');
      const response = await fetchApi.fetch(
        `${baseUrl}/apps/${encodeURIComponent(app)}/${action}`,
        { method: 'POST' },
      );
      const body = (await response.json().catch(() => ({}))) as { error?: string; message?: string };
      if (!response.ok) {
        throw new Error(body.error ?? `Falha (${response.status})`);
      }
      setActionMessage(`${app}: ${body.message ?? `aplicação ${ACTION_DONE[action]}.`}`);
      await load();
      // O estado real chega em segundos; confere de novo sem esperar o ciclo.
      setTimeout(load, 1500);
      setTimeout(load, 3500);
    } catch (error) {
      setActionError(`${app}: ${(error as Error).message}`);
    } finally {
      setBusyApp(undefined);
    }
  };

  const columns: TableColumn<AppRow>[] = [
    {
      title: 'Status',
      field: 'status',
      render: row => {
        const info = statusInfo(row.status);
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontWeight: 600, color: info.color }}>
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                background: info.color,
                display: 'inline-block',
              }}
            />
            {info.label}
          </span>
        );
      },
    },
    {
      title: 'Aplicação',
      field: 'app',
      render: row => <Link to={`/catalog/default/component/${row.app}`}>{row.app}</Link>,
    },
    {
      title: 'Endereços',
      field: 'url',
      render: row => {
        const links = row.links ?? [];
        if (!links.length) return '-';
        return (
          <div>
            {links.map(link => (
              <div key={link.url} style={{ whiteSpace: 'nowrap' }}>
                <Tooltip title={link.label}>
                  <a
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={row.status === 'stopped' ? { color: '#6b7280' } : undefined}
                  >
                    {link.url}
                  </a>
                </Tooltip>
              </div>
            ))}
          </div>
        );
      },
    },
    {
      title: 'Réplicas',
      field: 'readyReplicas',
      render: row => `${row.readyReplicas}/${row.desiredReplicas}`,
    },
    {
      title: 'CPU',
      field: 'cpuMillicores',
      render: row => formatCpu(row.cpuMillicores),
    },
    {
      title: 'Memória',
      field: 'memoryMiB',
      render: row => formatMemory(row.memoryMiB),
    },
    {
      title: 'Versão',
      field: 'commit',
      render: row =>
        row.commit ? <span style={{ fontFamily: 'monospace' }}>{row.commit}</span> : '-',
    },
    {
      title: 'Implantada',
      field: 'deployedAt',
      render: row => (
        <span title={row.deployedAt ? new Date(row.deployedAt).toLocaleString('pt-BR') : undefined}>
          {timeAgo(row.deployedAt)}
        </span>
      ),
    },
    {
      title: 'Último pipeline',
      field: 'lastRunResult',
      render: row =>
        row.lastRunResult ? (
          <span
            title={row.lastRunFinishedAt ? new Date(row.lastRunFinishedAt).toLocaleString('pt-BR') : undefined}
            style={{ color: RESULT_COLOR[row.lastRunResult] ?? 'inherit', fontWeight: 600 }}
          >
            {row.lastRunResult}
          </span>
        ) : (
          '-'
        ),
    },
    {
      title: 'Ações',
      field: 'app',
      sorting: false,
      render: row => {
        const busy = busyApp === row.app;
        const available = actionAvailability(row.status, busy);
        return (
          <>
            <Tooltip
              title={
                available.toggle === 'start'
                  ? 'Ligar'
                  : available.toggle === 'stop'
                    ? 'Desligar'
                    : 'Aguarde a transição terminar'
              }
            >
              <span>
                <IconButton
                  size="small"
                  aria-label={available.toggle === 'start' ? `Ligar ${row.app}` : `Desligar ${row.app}`}
                  disabled={!available.toggle}
                  onClick={() => available.toggle && runAction(row.app, available.toggle)}
                >
                  {available.toggle === 'start' ? (
                    <PlayArrowIcon fontSize="small" style={{ color: '#15803d' }} />
                  ) : (
                    <StopIcon fontSize="small" style={available.toggle ? { color: '#b91c1c' } : undefined} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={row.status === 'stopped' ? 'Desligada: use Ligar' : 'Reiniciar'}>
              <span>
                <IconButton
                  size="small"
                  aria-label={`Reiniciar ${row.app}`}
                  disabled={!available.restart}
                  onClick={() => runAction(row.app, 'restart')}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Ver pipeline e log do último deploy">
              <span>
                <Link to="/pipelines">
                  <IconButton size="small" aria-label={`Pipeline de ${row.app}`}>
                    <AccountTreeIcon fontSize="small" />
                  </IconButton>
                </Link>
              </span>
            </Tooltip>
          </>
        );
      },
    },
  ];

  return (
    <Page themeId="tool">
      <Header
        title="Aplicações"
        subtitle="Aplicações publicadas pelos pipelines: ligue, desligue e acompanhe o consumo (sem novo build)"
      />
      <Content>
        {loadError && (
          <Typography color="error" style={{ marginBottom: 8 }}>
            {loadError}
          </Typography>
        )}
        {actionError && (
          <Typography color="error" style={{ marginBottom: 8 }}>
            {actionError}
          </Typography>
        )}
        {actionMessage && (
          <Typography style={{ color: '#15803d', marginBottom: 8 }}>{actionMessage}</Typography>
        )}
        {!apps && !loadError && <Progress />}
        {apps && apps.length === 0 && (
          <Paper style={{ padding: 24 }}>
            <Typography variant="h6" gutterBottom>
              Nenhuma aplicação implantada ainda
            </Typography>
            <Typography variant="body2">
              Cadastre um repositório na página <Link to="/pipelines">Pipelines</Link>. Depois do primeiro
              deploy bem-sucedido, a aplicação aparece aqui. Uma aplicação implantada antes desta tela
              existir aparece depois de um novo deploy (use "Executar agora" em Pipelines).
            </Typography>
          </Paper>
        )}
        {apps && apps.length > 0 && (
          <Table
            title={summary(apps)}
            columns={columns}
            data={apps}
            options={{ search: true, paging: apps.length > 20, padding: 'dense' }}
            actions={[
              {
                icon: () => <RefreshIcon />,
                tooltip: 'Atualizar agora',
                isFreeAction: true,
                onClick: () => load(),
              },
            ]}
          />
        )}
      </Content>
    </Page>
  );
};
